import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import {
  AlertTriangle,
  CircleDashed,
  Clock,
  Github,
  KeyRound,
  Plug,
  PlugZap,
  RefreshCw,
  UserX,
} from 'lucide-react'
import { Badge, Button, cn } from '@ludus/ui'
import type { PipelineOrigin } from '@ludus/core'
import { fetchConnection } from '@/lib/api/sources.ts'
import { useSyncRun } from '../Sync/useSyncRun.ts'
import { SyncProgress } from '../Sync/SyncProgress.tsx'
import { ConfirmSyncDialog } from '../Sync/ConfirmSyncDialog.tsx'
import { ConnectSourceDialog } from './ConnectSourceDialog.tsx'
import { DisconnectDialog } from './DisconnectDialog.tsx'

interface SourceSectionProps {
  pipelineId: string
  origin: PipelineOrigin
}

export function SourceSection({ pipelineId, origin }: SourceSectionProps) {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)

  const key = `/pipelines/${pipelineId}/connection`
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchConnection(pipelineId))

  const connection = data?.connection ?? null
  const Icon = connection?.adapterKind === 'github' ? Github : Plug

  const { run, active, start, refresh: refreshRun } = useSyncRun(pipelineId, connection !== null)
  const offAxis = run?.counts.offAxis ?? 0
  const unowned = run?.counts.unowned ?? 0

  // "Native" is a claim about the pipeline, and an unanswered read is not evidence for it.
  function summary(): string {
    if (isLoading)
      return t('pages.pipelines.connect.section.loading', {
        defaultValue: 'Reading the connection…',
      })
    if (error != null)
      return t('pages.pipelines.connect.section.unreadable', {
        defaultValue: 'The connection could not be read.',
      })
    if (connection === null)
      return origin === 'native'
        ? t('pages.pipelines.connect.section.native', {
            defaultValue: 'Deals in this pipeline are created in Ludus, and you own its stages.',
          })
        : t('pages.pipelines.connect.section.notConnected', {
            defaultValue: 'No source connected yet — connect one to mirror its stages and deals.',
          })
    return t('pages.pipelines.connect.section.synced', {
      ref: connection.externalRef,
      defaultValue: 'Synced from {{ref}}.',
    })
  }

  return (
    <section className="@container flex flex-col gap-3 rounded-xl border border-border bg-background-secondary p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            connection === null ? 'bg-background text-muted' : 'bg-primary-light text-primary',
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <h2 className="text-xs font-semibold text-foreground">
              {t('pages.pipelines.connect.section.title', { defaultValue: 'Source' })}
            </h2>
            {!isLoading && error == null && (
              <Badge tone={connection === null ? 'muted' : 'primary'} size="sm">
                {connection?.adapterKind ??
                  (origin === 'native'
                    ? t('pages.pipelines.connect.section.badgeNative', { defaultValue: 'Native' })
                    : t('pages.pipelines.connect.section.badgeExternal', {
                        defaultValue: 'External',
                      }))}
              </Badge>
            )}
          </div>
          <p className="truncate text-xs text-muted">{summary()}</p>
        </div>

        <div className="flex w-full shrink-0 items-center gap-2 @sm:w-auto">
          {connection !== null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={active}
              className="flex-1 @sm:flex-none"
              onClick={() => setConfirmOpen(true)}
            >
              <RefreshCw
                className={cn('mr-1.5 h-3.5 w-3.5', active && 'animate-spin')}
                aria-hidden="true"
              />
              {active
                ? t('pages.pipelines.connect.section.syncing', { defaultValue: 'Syncing…' })
                : t('pages.pipelines.connect.section.syncNow', { defaultValue: 'Sync now' })}
            </Button>
          )}

          {connection !== null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="flex-1 @sm:flex-none"
              onClick={() => setDisconnectOpen(true)}
            >
              <PlugZap className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {t('pages.pipelines.connect.section.disconnect', { defaultValue: 'Disconnect' })}
            </Button>
          )}

          {origin === 'external' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isLoading}
              className="flex-1 @sm:flex-none"
              onClick={() => setDialogOpen(true)}
            >
              {connection === null
                ? t('pages.pipelines.connect.section.connect', { defaultValue: 'Connect a source' })
                : t('pages.pipelines.connect.section.reconnect', { defaultValue: 'Reconnect' })}
            </Button>
          )}
        </div>
      </div>

      {connection !== null && (
        <dl className="flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-border pt-3 text-[11px]">
          <Meta
            icon={KeyRound}
            label={t('pages.pipelines.connect.section.token', { defaultValue: 'Token' })}
            value={data?.credentialHint ?? '—'}
            mono
          />
          <Meta
            icon={Clock}
            label={t('pages.pipelines.connect.section.lastSync', { defaultValue: 'Last sync' })}
            value={
              (run?.finishedAt ?? data?.lastSyncAt)?.toLocaleString() ??
              t('pages.pipelines.connect.section.never', { defaultValue: 'never' })
            }
          />
          {run?.state === 'done' && (
            <Meta
              icon={RefreshCw}
              label={t('pages.pipelines.connect.section.syncedCount', {
                defaultValue: 'Records synced',
              })}
              value={String(run.counts.reported ?? 0)}
            />
          )}
          {offAxis > 0 && (
            <Meta
              icon={CircleDashed}
              label={t('pages.pipelines.connect.section.offAxis', { defaultValue: 'In no column' })}
              value={String(offAxis)}
            />
          )}
          {unowned > 0 && (
            <Meta
              icon={UserX}
              label={t('pages.pipelines.connect.section.unowned', {
                defaultValue: 'Owner unmatched',
              })}
              value={String(unowned)}
            />
          )}
          {(run?.error ?? data?.lastSyncError) != null && (
            <span className="flex items-center gap-1.5 text-toast-error-text">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              {run?.error ?? data?.lastSyncError}
            </span>
          )}
        </dl>
      )}

      {active && run && <SyncProgress run={run} />}

      <ConfirmSyncDialog open={confirmOpen} onOpenChange={setConfirmOpen} onConfirm={start} />

      {connection !== null && (
        <DisconnectDialog
          open={disconnectOpen}
          onOpenChange={setDisconnectOpen}
          pipelineId={pipelineId}
          externalRef={connection.externalRef}
          onDisconnected={async () => {
            await mutate()
            await refreshRun()
          }}
        />
      )}

      <ConnectSourceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pipelineId={pipelineId}
        onConnected={() => {
          void mutate()
          void refreshRun()
        }}
      />
    </section>
  )
}

function Meta({
  icon: Icon,
  label,
  value,
  mono,
}: {
  icon: typeof Clock
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3 w-3 shrink-0 text-muted" aria-hidden="true" />
      <dt className="text-muted">{label}</dt>
      <dd className={cn('text-foreground', mono && 'font-mono')}>{value}</dd>
    </div>
  )
}
